import { useEffect, useRef, useState } from 'react'
import { Button } from '@forwardflow/ui-v2'
import {
   CalendarCheckIcon,
   CheckCircle02Icon,
   Message01Icon,
   PhoneIcon,
   PhoneRotaryIcon,
} from '@forwardflow/icons'
import GoogleCalendarIcon from '@forwardflow/assets/svg/GoogleCalendarIcon'
import Icon from '@forwardflow/ui/Icons'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { UserService } from '@forwardflow/services/UserService'
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import {
   FF_ASSISTANT_NUMBER,
   FF_ASSISTANT_NUMBER_PRETTY,
   isFreeFlow,
   isSandbox,
   useDevTools,
   wizardPath,
} from '../dev/devTools'
import { Card, LinkButton, StepFooter, StepHeader } from '../shell/Card'
import { prettyPhone } from '../shell/format'

const SENT_KEY = 'ff-onboarding:card-sent'

/** A vCard for the Forward Flow number, so "Save contact" works on any phone. */
const vcardUrl = () => {
   const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      'FN:Forward Flow',
      'ORG:Forward Flow',
      `TEL;TYPE=CELL:${FF_ASSISTANT_NUMBER}`,
      'NOTE:Live lead transfers and help by text.',
      'END:VCARD',
   ].join('\n')
   return `data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`
}

/**
 * Step 5: the Forward Flow number, and what it does.
 *
 * Texted to the customer's mobile on arrival (`/users/send-card`), shown
 * here with the three things to know: live transfers come from it, we book
 * an appointment when they cannot pick up (on their calendar once it is
 * connected, else into Follow Up Boss), and texting it gets live help.
 */
export const YourNumber = () => {
   const { currentOrganization } = useAppStore()
   const { onboardingData, getNextStep, getPreviousStep, setIsCalendarConnected } =
      useOnboardingStoreV2()
   const owner = onboardingData.invitedUsers?.find((u) => u.isOwner)
   const ownerPhone = owner?.phone || ''

   const { data: calendarInfo } = useGetUserCalenderInfo(
      Boolean(currentOrganization) && !isSandbox()
   )
   const [sandboxCalendar, setSandboxCalendar] = useState<'google' | 'microsoft' | null>(null)
   const connectedProvider = isSandbox()
      ? sandboxCalendar
      : calendarInfo?.email
        ? calendarInfo.provider === 'microsoft'
           ? 'microsoft'
           : 'google'
        : null
   const isCalendarConnected = Boolean(connectedProvider) || onboardingData.isCalendarConnected
   const providerLabel = connectedProvider === 'microsoft' ? 'Outlook Calendar' : 'Google Calendar'

   /* ---------- The text with the number ---------- */

   const [sent, setSent] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')
   const sendCard = async () => {
      setSent('sending')
      if (isFreeFlow()) {
         await new Promise((r) => setTimeout(r, 500))
         setSent('sent')
         return
      }
      try {
         await UserService.sendCard()
         setSent('sent')
         try {
            sessionStorage.setItem(SENT_KEY, currentOrganization || '1')
         } catch {
            // ignore
         }
      } catch {
         setSent('failed')
      }
   }
   // Send once per session on arrival, so the screen can say "we texted you".
   const autoSent = useRef(false)
   useEffect(() => {
      if (autoSent.current) return
      autoSent.current = true
      let already = false
      try {
         already = sessionStorage.getItem(SENT_KEY) === (currentOrganization || '1')
      } catch {
         // ignore
      }
      if (already) setSent('sent')
      else void sendCard()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   /* ---------- Calendar ---------- */

   const [connecting, setConnecting] = useState(false)
   const connectCalendar = async (provider: 'google' | 'microsoft') => {
      if (isSandbox()) {
         setSandboxCalendar(provider)
         setIsCalendarConnected(true)
         return
      }
      try {
         setConnecting(true)
         const redirectUrl = encodeURIComponent(`${window.location.origin}${wizardPath()}`)
         const response =
            provider === 'microsoft'
               ? await UserService.getConnectUserWithOutlookCalendar(
                    redirectUrl,
                    currentOrganization || ''
                 )
               : await UserService.getConnectUserWithCalender(redirectUrl, currentOrganization || '')
         if (response?.redirectUrl) window.location.href = response.redirectUrl
         else setConnecting(false)
      } catch {
         setConnecting(false)
      }
   }

   useDevTools({
      fill: () => setSent('sent'),
      skip: () => {
         getNextStep()
         return true
      },
   })

   const sentLine =
      sent === 'sent'
         ? `Texted to ${ownerPhone ? prettyPhone(ownerPhone) : 'your mobile'}.`
         : sent === 'sending'
           ? 'Sending the text now.'
           : sent === 'failed'
             ? "The text didn't go through."
             : ''

   return (
      <div className="space-y-4">
         <Card testId="your-number-card">
            <StepHeader
               title="We texted you a number. Save it."
               lede="This is the Forward Flow number. Interested leads reach you from it, and you can text it for help any time."
            />

            <div className="rounded-xl border border-[#9F7BFF] bg-[rgba(159,123,255,0.10)] p-4 flex flex-col sm:flex-row sm:items-center gap-4">
               <span className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-[#9F7BFF]/20 text-[#A684FF]">
                  <PhoneRotaryIcon width={22} height={22} />
               </span>
               <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-2xl leading-none" data-testid="ff-number">
                     {FF_ASSISTANT_NUMBER_PRETTY}
                  </p>
                  <p className="text-[#99A1AF] text-xs mt-1.5" data-testid="card-sent-line">
                     Forward Flow · {sentLine}
                  </p>
               </div>
               <div className="flex items-center gap-2 shrink-0">
                  <a
                     href={vcardUrl()}
                     download="Forward Flow.vcf"
                     className="inline-flex items-center h-9 px-3.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
                  >
                     Save contact
                  </a>
                  <LinkButton onClick={() => void sendCard()} disabled={sent === 'sending'}>
                     {sent === 'failed' ? 'Try again' : 'Text it again'}
                  </LinkButton>
               </div>
            </div>

            <ul className="space-y-3" data-testid="number-explainer">
               <li className="flex items-start gap-3">
                  <span className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-white/[0.05] text-[#A684FF]">
                     <PhoneIcon width={16} />
                  </span>
                  <div className="text-sm">
                     <p className="text-white font-medium">Interested leads are transferred to you live</p>
                     <p className="text-[#99A1AF] text-xs mt-0.5">
                        When a lead wants to talk, we call{' '}
                        {ownerPhone ? prettyPhone(ownerPhone) : 'your mobile'} from this number and
                        connect you. Pick up when you see it.
                     </p>
                  </div>
               </li>
               <li className="flex items-start gap-3">
                  <span className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-white/[0.05] text-[#A684FF]">
                     <CalendarCheckIcon width={16} />
                  </span>
                  <div className="text-sm flex-1 min-w-0">
                     <p className="text-white font-medium">Can't pick up? We book an appointment for you</p>
                     <p className="text-[#99A1AF] text-xs mt-0.5">
                        {isCalendarConnected
                           ? `Appointments go on your ${providerLabel}${calendarInfo?.email ? ` (${calendarInfo.email})` : ''} and into Follow Up Boss.`
                           : 'Connect a calendar and we book straight into it. Until then, every appointment is written into Follow Up Boss with the lead tagged, so nothing is missed.'}
                     </p>
                     <div className="mt-2 flex flex-wrap items-center gap-2">
                        {isCalendarConnected ? (
                           <span className="inline-flex items-center gap-1.5 text-[#00D492] text-xs font-medium">
                              <CheckCircle02Icon width={16} height={16} /> {providerLabel} connected
                           </span>
                        ) : (
                           <>
                              <Button
                                 size="middle"
                                 variant="secondary"
                                 className="!h-9 !px-3 !text-xs"
                                 onClick={() => void connectCalendar('google')}
                                 disabled={connecting}
                              >
                                 <GoogleCalendarIcon /> Connect Google Calendar
                              </Button>
                              <Button
                                 size="middle"
                                 variant="secondary"
                                 className="!h-9 !px-3 !text-xs"
                                 onClick={() => void connectCalendar('microsoft')}
                                 disabled={connecting}
                              >
                                 <Icon name="microsoft-icon" className="w-4" /> Connect Outlook
                              </Button>
                           </>
                        )}
                     </div>
                  </div>
               </li>
               <li className="flex items-start gap-3">
                  <span className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-white/[0.05] text-[#A684FF]">
                     <Message01Icon width={16} />
                  </span>
                  <div className="text-sm">
                     <p className="text-white font-medium">Text this number for help, any time</p>
                     <p className="text-[#99A1AF] text-xs mt-0.5">
                        Ask anything: how a campaign is doing, what a lead said, help with a setting,
                        or something from your Follow Up Boss data. You get an answer by text.
                     </p>
                  </div>
               </li>
            </ul>
         </Card>

         <StepFooter
            onBack={() => getPreviousStep()}
            action="Got it, continue"
            onAction={() => getNextStep()}
            note="Next: which leads we should leave alone."
            testId="continue-number"
         />
      </div>
   )
}
